/**
 * Export Utilities
 * 
 * Utility functions for exporting data to Excel, PDF, and CSV formats
 * Used by Ant Design tables for manual export functionality
 */

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ========== TYPES ==========

export type ExportFormat = 'excel' | 'pdf' | 'csv';

export interface ExportColumn {
  key: string;
  title: string;
  width?: number;
}

export interface MetadataRow {
  label: string;
  value: string;
}

export interface ExportOptions {
  fileName: string;
  sheetName?: string;
  title?: string;
  metadataRows?: MetadataRow[];
  /** PDF only: full URL to company logo image */
  companyLogoUrl?: string | null;
  /** PDF only: company name shown next to logo (or alone if no logo) */
  companyName?: string | null;
}

/**
 * Get natural dimensions of an image from its data URL.
 */
function getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Load a remote image and return a PNG data URL for jsPDF addImage.
 * Tries fetch first, then Image + canvas (handles some CORS cases).
 */
export async function loadImageAsPngDataUrlForPdf(url: string): Promise<string | null> {
  const trimmed = url?.trim();
  if (!trimmed || typeof document === 'undefined') {
    return null;
  }
  // try {
  //   const response = await fetch(trimmed, { mode: 'cors', credentials: 'omit' });
  //   if (!response.ok) {
  //     throw new Error('fetch failed');
  //   }
  //   const blob = await response.blob();
  //   if (typeof createImageBitmap === 'function') {
  //     const bitmap = await createImageBitmap(blob);
  //     try {
  //       const canvas = document.createElement('canvas');
  //       canvas.width = bitmap.width;
  //       canvas.height = bitmap.height;
  //       const ctx = canvas.getContext('2d');
  //       if (!ctx) return null;
  //       ctx.drawImage(bitmap, 0, 0);
  //       return canvas.toDataURL('image/png');
  //     } finally {
  //       bitmap.close();
  //     }
  //   }
  // } catch {
  //   // fall through to Image()
  // }
    // const img = new Image();
    // img.crossOrigin = 'anonymous';
    // img.onload = () => {
    //   try {
    //     const canvas = document.createElement('canvas');
    //     canvas.width = img.naturalWidth;
    //     canvas.height = img.naturalHeight;
    //     const ctx = canvas.getContext('2d');
    //     if (!ctx) {
    //       return;
    //     }
    //     ctx.drawImage(img, 0, 0);
    //     //resolve(canvas.toDataURL('image/png'));
    //   } catch {
    //     //resolve(null);
    //   }
    // };
    // //img.onerror = () => resolve(null);
    // img.src = trimmed;
    return "data:image/png;base64," + trimmed;
}

// ========== HELPER FUNCTIONS ==========

/**
 * Convert HTML content to plain text
 * Handles various HTML elements and entities gracefully
 * @param html - The HTML string to convert
 * @returns Plain text string
 */
export const htmlToPlainText = (html: string | null | undefined): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  // Check if the string contains HTML tags
  const hasHtmlTags = /<[^>]+>/g.test(html);
  
  if (!hasHtmlTags) {
    // Already plain text, just decode any HTML entities
    return decodeHtmlEntities(html).trim();
  }
  
  // Create a temporary DOM element to parse HTML
  // This works in browser environment
  if (typeof document !== 'undefined') {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Replace <br> and block elements with newlines
    const blockElements = tempDiv.querySelectorAll('p, div, br, li, h1, h2, h3, h4, h5, h6, tr');
    blockElements.forEach(el => {
      if (el.tagName.toLowerCase() === 'br') {
        el.replaceWith('\n');
      } else if (el.tagName.toLowerCase() === 'li') {
        el.prepend(document.createTextNode('• '));
        el.append(document.createTextNode('\n'));
      } else {
        el.append(document.createTextNode('\n'));
      }
    });
    
    // Get text content and clean up
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // Clean up multiple newlines and whitespace
    text = text
      .replace(/\n{3,}/g, '\n\n') // Replace 3+ newlines with 2
      .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space
      .trim();
    
    return text;
  }
  
  // Fallback for non-browser environment (SSR)
  return fallbackHtmlToText(html);
};

/**
 * Decode HTML entities
 */
const decodeHtmlEntities = (text: string): string => {
  const entities: Record<string, string> = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&ndash;': '–',
    '&mdash;': '—',
    '&hellip;': '…',
    '&bull;': '•',
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
  };
  
  let result = text;
  for (const [entity, char] of Object.entries(entities)) {
    result = result.replace(new RegExp(entity, 'gi'), char);
  }
  
  // Handle numeric entities
  result = result.replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)));
  result = result.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  
  return result;
};

/**
 * Fallback HTML to text conversion using regex (for non-browser environments)
 */
const fallbackHtmlToText = (html: string): string => {
  let text = html
    // Replace block elements with newlines
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n\n')
    .replace(/<li[^>]*>/gi, '• ')
    // Remove remaining HTML tags
    .replace(/<[^>]+>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // Clean up whitespace
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
  
  return text;
};

/**
 * Generate timestamp string for file names
 */
const getTimestamp = (): string => {
  const now = new Date();
  return now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
};

/**
 * Extract data for selected columns
 * Automatically converts HTML content to plain text
 */
const extractData = <T extends Record<string, unknown>>(
  data: T[],
  columns: ExportColumn[]
): Record<string, unknown>[] => {
  return data.map(row => {
    const extractedRow: Record<string, unknown> = {};
    columns.forEach(col => {
      const value = row[col.key];
      // Handle nested objects, arrays, and special values
      if (value === null || value === undefined) {
        extractedRow[col.title] = '';
      } else if (typeof value === 'object') {
        extractedRow[col.title] = JSON.stringify(value);
      } else if (typeof value === 'string') {
        // Automatically convert HTML to plain text for string values
        // Replace newlines with spaces (Excel/CSV don't render \n inline)
        extractedRow[col.title] = htmlToPlainText(value).replace(/\n/g, ' ');
      } else {
        extractedRow[col.title] = value;
      }
    });
    return extractedRow;
  });
};

// ========== EXPORT FUNCTIONS ==========

/**
 * Export data to Excel (.xlsx) format
 */
export const exportToExcel = <T extends Record<string, unknown>>(
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions
): void => {
  const { fileName, sheetName = 'Sheet1', metadataRows } = options;
  
  // Extract data with column titles as keys
  const exportData = extractData(data, columns);
  
  const workbook = XLSX.utils.book_new();

  if (metadataRows && metadataRows.length > 0) {
    const metaAoa: (string | undefined)[][] = [];

    metadataRows.forEach(row => {
      metaAoa.push([`${row.label}:`, htmlToPlainText(row.value)]);
    });
    metaAoa.push([]); // blank separator row

    const worksheet = XLSX.utils.aoa_to_sheet(metaAoa);

    // Append the table data starting after metadata + blank row
    XLSX.utils.sheet_add_json(worksheet, exportData, {
      origin: `A${metaAoa.length + 1}`,
    });

    // Set column widths (use table column widths, ensure at least 2 columns for metadata)
    const colWidths = columns.map(col => ({ wch: col.width || 20 }));
    if (colWidths.length < 2) {
      colWidths.push({ wch: 40 });
    }
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  } else {
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const colWidths = columns.map(col => ({ wch: col.width || 20 }));
    worksheet['!cols'] = colWidths;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  }
  
  // Generate buffer and save
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  saveAs(blob, `${fileName}_${getTimestamp()}.xlsx`);
};

/**
 * Export data to CSV format
 */
export const exportToCSV = <T extends Record<string, unknown>>(
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions
): void => {
  const { fileName } = options;
  
  // Extract data with column titles as keys
  const exportData = extractData(data, columns);
  
  // Create workbook and worksheet
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  
  // Generate CSV and save
  const csvContent = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  
  saveAs(blob, `${fileName}_${getTimestamp()}.csv`);
};

/**
 * Calculate optimal PDF settings based on number of columns
 */
const getPDFSettings = (columnCount: number) => {
  // Dynamic settings based on column count
  if (columnCount <= 5) {
    return {
      format: 'a4' as const,
      fontSize: 9,
      headerFontSize: 9,
      cellPadding: 3,
      margin: 14,
    };
  } else if (columnCount <= 8) {
    return {
      format: 'a4' as const,
      fontSize: 8,
      headerFontSize: 8,
      cellPadding: 2,
      margin: 10,
    };
  } else if (columnCount <= 12) {
    return {
      format: 'a3' as const, // Use A3 for many columns
      fontSize: 7,
      headerFontSize: 7,
      cellPadding: 2,
      margin: 10,
    };
  } else {
    // For very wide tables (like Gaps with 20+ columns)
    return {
      format: 'a3' as const,
      fontSize: 6,
      headerFontSize: 6,
      cellPadding: 1.5,
      margin: 8,
    };
  }
};

/**
 * Calculate column widths based on content
 */
const calculateColumnWidths = (
  headers: string[],
  rows: string[][],
  availableWidth: number
): number[] => {
  const columnCount = headers.length;
  
  // Calculate max content length for each column
  const maxLengths = headers.map((header, colIndex) => {
    const headerLength = header.length;
    const maxContentLength = rows.reduce((max, row) => {
      const cellLength = (row[colIndex] || '').length;
      return Math.max(max, cellLength);
    }, 0);
    return Math.max(headerLength, Math.min(maxContentLength, 50)); // Cap at 50 chars
  });
  
  // Calculate total weight
  const totalWeight = maxLengths.reduce((sum, len) => sum + len, 0);
  
  // Distribute width proportionally, with minimum width
  const minWidth = availableWidth / columnCount * 0.5; // Minimum 50% of equal distribution
  const widths = maxLengths.map(len => {
    const proportionalWidth = (len / totalWeight) * availableWidth;
    return Math.max(proportionalWidth, minWidth);
  });
  
  // Normalize to fit available width
  const totalWidth = widths.reduce((sum, w) => sum + w, 0);
  const scale = availableWidth / totalWidth;
  
  return widths.map(w => w * scale);
};

/**
 * Export data to PDF format
 * Optimized for tables with many columns
 */
export const exportToPDF = async <T extends Record<string, unknown>>(
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions
): Promise<void> => {
  const { fileName, title, companyLogoUrl, companyName } = options;
  const columnCount = columns.length;
  
  // Get optimal settings based on column count
  const settings = getPDFSettings(columnCount);
  
  // Create PDF document (always landscape for tables)
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: settings.format,
  });
  
  // Get page dimensions
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const availableWidth = pageWidth - (settings.margin * 2);
  
  // --- Header: logo + company name row, then title row ---
  const LOGO_HEIGHT = 10; // mm
  const LOGO_MAX_WIDTH = 30; // mm
  let currentY = settings.margin;

  let logoDataUrl: string | null = null;
  if (companyLogoUrl?.trim()) {
    logoDataUrl = await loadImageAsPngDataUrlForPdf(companyLogoUrl.trim());
  }
  const companyLine = companyName?.trim() || '';
  const hasBranding = !!(logoDataUrl || companyLine);

  if (hasBranding) {
    let brandX = settings.margin;
    if (logoDataUrl) {
      let logoW = LOGO_HEIGHT;
      try {
        const dims = await getImageDimensions(logoDataUrl);
        const ratio = dims.width / dims.height;
        logoW = Math.min(LOGO_HEIGHT * ratio, LOGO_MAX_WIDTH);
      } catch { /* fallback to square */ }
      doc.addImage(logoDataUrl, 'PNG', brandX, currentY - 1, logoW, LOGO_HEIGHT);
      brandX += logoW + 3;
    }
    if (companyLine) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const textY = logoDataUrl ? currentY + LOGO_HEIGHT / 2 + 1 : currentY + 4;
      doc.text(companyLine, brandX, textY);
    }
    currentY += (logoDataUrl ? LOGO_HEIGHT + 2 : 6);
  }

  // Export timestamp (top-right)
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(128, 128, 128);
  const timestamp = new Date().toLocaleString();
  doc.text(`Exported: ${timestamp}`, pageWidth - settings.margin, settings.margin + 3, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  if (title) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, settings.margin, currentY + 5);
    currentY += 10;
  }

  // Add metadata rows if provided
  const { metadataRows } = options;
  if (metadataRows && metadataRows.length > 0) {
    const lineHeight = 5;
    doc.setFontSize(10);
    metadataRows.forEach(row => {
      const plainValue = htmlToPlainText(row.value) || '-';
      doc.setFont('helvetica', 'bold');
      const label = `${row.label}: `;
      const labelWidth = doc.getTextWidth(label);
      doc.text(label, settings.margin, currentY);
      doc.setFont('helvetica', 'normal');

      const valueMaxWidth = availableWidth - labelWidth;
      const wrappedLines: string[] = doc.splitTextToSize(plainValue, valueMaxWidth);

      if (wrappedLines.length <= 1) {
        doc.text(plainValue, settings.margin + labelWidth, currentY);
        currentY += lineHeight + 1;
      } else {
        doc.text(wrappedLines[0], settings.margin + labelWidth, currentY);
        currentY += lineHeight;
        for (let i = 1; i < wrappedLines.length; i++) {
          if (currentY + lineHeight > pageHeight - settings.margin) {
            doc.addPage();
            currentY = settings.margin;
          }
          doc.text(wrappedLines[i], settings.margin + labelWidth, currentY);
          currentY += lineHeight;
        }
        currentY += 1;
      }
    });
    currentY += 4;
  }
  
  // Prepare table data (with automatic HTML to plain text conversion)
  const headers = columns.map(col => col.title);
  const rows = data.map(row => 
    columns.map(col => {
      const value = row[col.key];
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') return JSON.stringify(value);
      // Convert HTML to plain text for string values
      if (typeof value === 'string') return htmlToPlainText(value);
      return String(value);
    })
  );
  
  // Calculate optimal column widths
  const columnWidths = calculateColumnWidths(headers, rows, availableWidth);
  
  // Create column styles with calculated widths
  const columnStyles: { [key: number]: { cellWidth: number } } = {};
  columnWidths.forEach((width, index) => {
    columnStyles[index] = { cellWidth: width };
  });
  
  // Add table using autoTable with optimized settings
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: currentY,
    styles: {
      fontSize: settings.fontSize,
      cellPadding: settings.cellPadding,
      overflow: 'linebreak',
      font: 'helvetica',
      lineWidth: 0.1,
      lineColor: [229, 231, 235], // Gray border
      valign: 'middle',
    },
    headStyles: {
      fillColor: [37, 99, 235], // Blue (#2563EB)
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: settings.headerFontSize,
      halign: 'left',
      valign: 'middle',
      minCellHeight: 8,
    },
    bodyStyles: {
      minCellHeight: 6,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251], // Gray (#F9FAFB)
    },
    columnStyles,
    margin: { 
      top: settings.margin, 
      right: settings.margin, 
      bottom: settings.margin, 
      left: settings.margin 
    },
    tableWidth: 'auto',
    // Add page numbers
    didDrawPage: (data) => {
      const pageCount = doc.getNumberOfPages();
      const currentPage = data.pageNumber;
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Page ${currentPage} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: 'center' }
      );
    },
  });
  
  // Save PDF
  doc.save(`${fileName}_${getTimestamp()}.pdf`);
};

/**
 * Main export function - routes to appropriate format handler
 */
export const exportData = async <T extends Record<string, unknown>>(
  format: ExportFormat,
  data: T[],
  columns: ExportColumn[],
  options: ExportOptions
): Promise<void> => {
  switch (format) {
    case 'excel':
      exportToExcel(data, columns, options);
      break;
    case 'csv':
      exportToCSV(data, columns, options);
      break;
    case 'pdf':
      await exportToPDF(data, columns, options);
      break;
    default:
      console.error(`Unsupported export format: ${format}`);
  }
};

// ========== MULTI-SECTION EXPORT ==========

export interface ExportSection {
  data: Record<string, unknown>[];
  columns: ExportColumn[];
  sheetName: string;
  title: string;
}

/**
 * Export multiple datasets into a single file.
 * Excel: each section becomes a separate sheet.
 * PDF: sections are rendered as sequential tables in one document.
 */
export const exportMultiSectionData = async (
  format: ExportFormat,
  sections: ExportSection[],
  options: ExportOptions
): Promise<void> => {
  if (format === 'excel') {
    exportMultiSectionToExcel(sections, options);
  } else if (format === 'pdf') {
    await exportMultiSectionToPDF(sections, options);
  }
};

const exportMultiSectionToExcel = (
  sections: ExportSection[],
  options: ExportOptions
): void => {
  const { fileName, metadataRows } = options;
  const workbook = XLSX.utils.book_new();

  sections.forEach(section => {
    const sectionData = extractData(section.data, section.columns);

    if (metadataRows && metadataRows.length > 0) {
      const metaAoa: (string | undefined)[][] = [];
      metadataRows.forEach(row => {
        metaAoa.push([`${row.label}:`, htmlToPlainText(row.value)]);
      });
      metaAoa.push([]);

      const worksheet = XLSX.utils.aoa_to_sheet(metaAoa);
      XLSX.utils.sheet_add_json(worksheet, sectionData, {
        origin: `A${metaAoa.length + 1}`,
      });

      const colWidths = section.columns.map(col => ({ wch: col.width || 20 }));
      if (colWidths.length < 2) colWidths.push({ wch: 40 });
      worksheet['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, section.sheetName);
    } else {
      const worksheet = XLSX.utils.json_to_sheet(sectionData);
      const colWidths = section.columns.map(col => ({ wch: col.width || 20 }));
      worksheet['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, section.sheetName);
    }
  });

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${fileName}_${getTimestamp()}.xlsx`);
};

const exportMultiSectionToPDF = async (
  sections: ExportSection[],
  options: ExportOptions
): Promise<void> => {
  const { fileName, title, companyLogoUrl, companyName } = options;

  const maxCols = Math.max(...sections.map(s => s.columns.length));
  const settings = getPDFSettings(maxCols);

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: settings.format,
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const availableWidth = pageWidth - settings.margin * 2;

  const LOGO_HEIGHT = 10;
  const LOGO_MAX_WIDTH = 30;
  let currentY = settings.margin;

  let logoDataUrl: string | null = null;
  if (companyLogoUrl?.trim()) {
    logoDataUrl = await loadImageAsPngDataUrlForPdf(companyLogoUrl.trim());
  }
  const companyLine = companyName?.trim() || '';
  const hasBranding = !!(logoDataUrl || companyLine);

  if (hasBranding) {
    let brandX = settings.margin;
    if (logoDataUrl) {
      let logoW = LOGO_HEIGHT;
      try {
        const dims = await getImageDimensions(logoDataUrl);
        const ratio = dims.width / dims.height;
        logoW = Math.min(LOGO_HEIGHT * ratio, LOGO_MAX_WIDTH);
      } catch { /* fallback to square */ }
      doc.addImage(logoDataUrl, 'PNG', brandX, currentY - 1, logoW, LOGO_HEIGHT);
      brandX += logoW + 3;
    }
    if (companyLine) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const textY = logoDataUrl ? currentY + LOGO_HEIGHT / 2 + 1 : currentY + 4;
      doc.text(companyLine, brandX, textY);
    }
    currentY += (logoDataUrl ? LOGO_HEIGHT + 2 : 6);
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(128, 128, 128);
  const timestamp = new Date().toLocaleString();
  doc.text(`Exported: ${timestamp}`, pageWidth - settings.margin, settings.margin + 3, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  if (title) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, settings.margin, currentY + 5);
    currentY += 10;
  }

  sections.forEach((section, sectionIdx) => {
    if (sectionIdx > 0) {
      const spaceNeeded = 50;
      if (currentY + spaceNeeded > pageHeight - settings.margin) {
        doc.addPage();
        currentY = settings.margin;
      }
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text(section.title, settings.margin, currentY);
    currentY += 6;
    doc.setTextColor(0, 0, 0);

    const headers = section.columns.map(col => col.title);
    const rows = section.data.map(row =>
      section.columns.map(col => {
        const value = row[col.key];
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        if (typeof value === 'string') return htmlToPlainText(value);
        return String(value);
      })
    );

    const sectionSettings = getPDFSettings(section.columns.length);
    const columnWidths = calculateColumnWidths(headers, rows, availableWidth);
    const columnStyles: { [key: number]: { cellWidth: number } } = {};
    columnWidths.forEach((width, index) => {
      columnStyles[index] = { cellWidth: width };
    });

    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: currentY,
      styles: {
        fontSize: sectionSettings.fontSize,
        cellPadding: sectionSettings.cellPadding,
        overflow: 'linebreak',
        font: 'helvetica',
        lineWidth: 0.1,
        lineColor: [229, 231, 235],
        valign: 'middle',
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: sectionSettings.headerFontSize,
        halign: 'left',
        valign: 'middle',
        minCellHeight: 8,
      },
      bodyStyles: { minCellHeight: 6 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles,
      margin: {
        top: settings.margin,
        right: settings.margin,
        bottom: settings.margin,
        left: settings.margin,
      },
      tableWidth: 'auto',
      didDrawPage: (data) => {
        const pageCount = doc.getNumberOfPages();
        const currentPage = data.pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      },
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    currentY = (doc as any).lastAutoTable?.finalY + 10 || currentY + 20;
  });

  doc.save(`${fileName}_${getTimestamp()}.pdf`);
};

export default {
  exportToExcel,
  exportToCSV,
  exportToPDF,
  exportData,
  exportMultiSectionData,
  htmlToPlainText,
};
